import React, { useEffect, useMemo, useState /*useCallback*/ } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import '../Reconcile/Reconcile.scss'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import LeftArrow from '@tecuity/sos-icons/react/left-arrow'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import {
  getBatchReport,
  getPaymentBatches,
  getReconcileUsers,
  reconcileBatches,
} from '../Reconcile/ReconcileActions'
import { useHistory } from 'react-router'
import SelectBatches from './ReconcileSteps/SelectBatches'
import EnterTotals from './ReconcileSteps/EnterTotals'
import SelectReports from './ReconcileSteps/SelectReports'
import StepButton from '../StepButton'
import { setErrorToast } from '../../../actions'
import { CLEAR_RECONCILE_PAYMENT_BATCH } from '../Reconcile/ReconcileReducer'
import { pdfTypes, ADD_PDF_TO_LIST } from '../../ReportViewer/ReportViewerReducer'

const ReconcilePaymentBatch = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { users, selectedBatches, batchInfo, /*batchReports,*/ batchNums } = useSelector(
    (state) => state.reconcile,
    shallowEqual
  )
  const { WEB_USER_ID, appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const [step, setStep] = useState(1)
  const [selectedUserID, setSelectedUserID] = useState(WEB_USER_ID)

  useEffect(() => {
    dispatch(getReconcileUsers())
    return () => {
      dispatch({ type: CLEAR_RECONCILE_PAYMENT_BATCH })
    }
  }, [])

  useEffect(() => {
    selectedUserID && dispatch(getPaymentBatches(selectedUserID, null))
  }, [selectedUserID])

  useEffect(() => {
    setSelectedUserID(WEB_USER_ID)
  }, [users, WEB_USER_ID])

  useEffect(() => {
    if (step === 3) {
      dispatch({
        type: ADD_PDF_TO_LIST,
        pdfInfo: {
          filename: `Reconcile-${selectedUserID}.pdf`,
          type: pdfTypes.RECONCILE_REPORT,
          selectedUserID: selectedUserID,
          batchNums: batchNums,
        },
      })
    }
  }, [step])

  const nextLabel = useMemo(() => {
    switch (step) {
      case 1:
        return 'NEXT'
      case 2:
        return 'RECONCILE'
      case 3:
        return 'PRINT'
      default:
        return 'NEXT'
    }
  }, [step])

  const reconcileUserOptions = useMemo(() => {
    return (users || []).map((user) => {
      return { label: user.Name, value: user.ID }
    })
  }, [users])

  const nextStepOnClick = () => {
    if (step === 1) {
      if (selectedBatches.length === 0) {
        // show error to have at least one selected.
        dispatch(setErrorToast('Must have at least 1 batch selected.'))
        return
      } else {
        setStep(2)
      }
    }

    if (step === 2) {
      let foundInfoDesc = ''
      selectedBatches.forEach((id) => {
        if (batchInfo[id]) {
          batchInfo[id].forEach((info) => {
            if (
              !info.AmountOnHand ||
              Number(info.Amount) !== Number(info.AmountOnHand.replace(',', '').replace('$', ''))
            ) {
              foundInfoDesc += foundInfoDesc.length === 0 ? info.Desc : ', ' + info.Desc
            }
          })
        }
      })

      dispatch(
        reconcileBatches(selectedUserID, (err) => {
          if (!err) {
            dispatch(getBatchReport(selectedUserID))
            setStep(3)
          }
        })
      )
    }

    if (step === 3) {
      dispatch({
        type: ADD_PDF_TO_LIST,
        pdfInfo: {
          filename: `Reconcile-${selectedUserID}.pdf`,
          type: pdfTypes.RECONCILE_REPORT,
          selectedUserID: selectedUserID,
          batchNums: batchNums,
        },
      })
    }
  }

  const previousStepOnClick = () => {
    if (step > 1) {
      setStep((curstep) => curstep - 1)
    }
  }

  return (
    <React.Fragment>
      <h2 className="accounting-form-header">Reconcile Payment Batch</h2>
      <div className="reconcile-wrapper">
        <div className="steps-sidebar">
          <StepButton
            active={step === 1}
            isValid={step > 1 ? true : undefined}
            title="Select Batches"
            stepIndex={1}
            totalSteps={3}
            setStep={setStep}
          />
          <StepButton
            active={step === 2}
            isValid={step > 2 ? true : undefined}
            title="Enter Totals"
            stepIndex={2}
            totalSteps={3}
            setStep={setStep}
          />
          <StepButton
            active={step === 3}
            isValid={step > 2 ? true : undefined}
            title="Report Summary"
            stepIndex={3}
            totalSteps={3}
            setStep={setStep}
          />
        </div>
        <div className="form-wrapper">
          <SelectBox
            label="App User"
            customCss="max-width: 250px"
            value={selectedUserID}
            onChange={(e) => setSelectedUserID(e)}
            options={reconcileUserOptions}
            disabled={
              reconcileUserOptions.length === 1 ||
              step !== 1 ||
              !appUserRights.includes('ReceiptReconcileOthersBatch')
                ? true
                : undefined
            }
          />
          {step === 1 && <SelectBatches />}
          {step === 2 && <EnterTotals selectedUserID={selectedUserID} />}
          {step === 3 && <SelectReports />}
        </div>
        <div className="context-bar-wrapper">
          <ContextBar>
            <div className="context-button-wrapper">
              <ContextButton variant="secondary" onClick={() => history.push('/accounting')}>
                {step === 3 ? 'Close' : 'Cancel'}
              </ContextButton>
              <ContextButton
                className={step === 1 || step === 3 ? 'hidden' : ''}
                onClick={previousStepOnClick}
                variant="secondary"
                leftIcon={
                  <div className="arrow-wrapper left">
                    <LeftArrow />
                  </div>
                }
              >
                Previous
              </ContextButton>
              {step != 3 && (
                <ContextButton
                  variant="primary"
                  onClick={nextStepOnClick}
                  rightIcon={
                    <div className="arrow-wrapper right">
                      {' '}
                      <RightArrow />
                    </div>
                  }
                >
                  {nextLabel}
                </ContextButton>
              )}
            </div>
          </ContextBar>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ReconcilePaymentBatch
